import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAction, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Classifier } from '../scanner/classifier';
import DMCAEmailModal from './DMCAEmailModal';
import { decodeUrl } from '../lib/urlUtils';
import RegionalBadges from './RegionalBadges';
import './ViolationCard.css';

interface ViolationCardProps {
  violation: any; // Convex violation object
  contentTitle: string;
  onToggleSelect?: (id: string) => void;
  isSelected?: boolean;
}

export default function ViolationCard({ violation, contentTitle, onToggleSelect, isSelected }: ViolationCardProps) {
  const [showScreenshot, setShowScreenshot] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  const [showDmcaModal, setShowDmcaModal] = useState(false);
  const [reachability, setReachability] = useState<'unknown'|'reachable'|'unreachable'>('unknown');
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(violation.screenshotUrl ?? null);
  const [captureState, setCaptureState] = useState<'idle'|'loading'|'success'|'error'>('idle');
  const [captureMessage, setCaptureMessage] = useState<string | null>(null);
  const evidenceBodyRef = useRef<HTMLDivElement>(null);
  const markReviewed = useMutation(api.violations.markReviewed);
  const markNotPirate = useMutation(api.violations.markNotPirate);
  const restoreViolation = useMutation(api.violations.restoreViolation);
  const captureScreenshot = useAction(api.violations.captureScreenshotForViolation);

  const confidenceLevel = Classifier.getConfidenceLevel(violation.confidenceScore);
  const confidenceColor = Classifier.getConfidenceColor(violation.confidenceScore);

  useEffect(() => {
    setScreenshotUrl(violation.screenshotUrl ?? null);
    setCaptureState('idle');
    setCaptureMessage(null);
  }, [violation.screenshotUrl]);

  // Lock body scroll and reset drawer scroll when evidence drawer opens
  useEffect(() => {
    if (showEvidence) {
      // Lock body scroll to prevent scrolling page behind drawer
      document.body.style.overflow = 'hidden';

      // Scroll the drawer content (not the page!) to top
      if (evidenceBodyRef.current) {
        evidenceBodyRef.current.scrollTop = 0;
      }
    } else {
      // Restore body scroll
      document.body.style.overflow = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [showEvidence]);

  const handleMarkReviewed = async () => {
    await markReviewed({
      violationId: violation._id,
      reviewed: !violation.reviewed,
    });
  };

  const handleMarkNotPirate = async () => {
    const reason = window.prompt('Optional reason (notes) for marking as not pirate:') || undefined;
    await markNotPirate({ violationId: violation._id, reason });
  };

  const handleCaptureScreenshot = async () => {
    setCaptureState('loading');
    setCaptureMessage(null);
    try {
      const result = await captureScreenshot({ violationId: violation._id });
      if (result && (result as any).success && (result as any).screenshotUrl) {
        const url = (result as any).screenshotUrl as string;
        setScreenshotUrl(url);
        setShowScreenshot(true);
        setCaptureState('success');
        setCaptureMessage('Screenshot captured');
      } else {
        setCaptureState('error');
        setCaptureMessage(((result as any)?.error as string) || 'Capture failed');
      }
    } catch (err: any) {
      setCaptureState('error');
      setCaptureMessage(err?.message || 'Capture failed');
    }
  };

  // Client reachability probe (session cached)
  const probeReachability = async (host: string) => {
    try {
      const key = `reach:${host}`;
      const cached = sessionStorage.getItem(key);
      if (cached === 'reachable' || cached === 'unreachable') {
        return cached as 'reachable'|'unreachable';
      }
    } catch {}

    const setAndCache = (status: 'reachable'|'unreachable') => {
      try { sessionStorage.setItem(`reach:${host}`, status); } catch {}
      return status;
    };

    // Try image loading (best for CORS-restricted sites)
    const tryImage = (url: string, timeoutMs = 5000) => new Promise<boolean>(resolve => {
      const img = new Image();
      let done = false;
      const finish = (ok: boolean) => {
        if (done) return;
        done = true;
        img.src = ''; // Clear to stop loading
        resolve(ok);
      };
      img.onload = () => finish(true);
      img.onerror = () => finish(false);
      img.src = url;
      setTimeout(() => finish(false), timeoutMs);
    });

    // Try no-cors fetch (succeeds if server responds, even with CORS block)
    const tryFetch = async (url: string, timeoutMs = 5000) => {
      try {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), timeoutMs);
        await fetch(url, { mode: 'no-cors', method: 'HEAD', signal: ctrl.signal });
        clearTimeout(t);
        return true;
      } catch (err: any) {
        // AbortError means timeout (unreachable)
        // Other errors (TypeError, etc.) often mean CORS block (site exists but blocks us)
        return err.name !== 'AbortError';
      }
    };

    try {
      // Strategy: Try multiple common endpoints that piracy sites usually have
      const commonPaths = [
        '/favicon.ico',
        '/logo.png',
        '/assets/logo.png',
        '/images/logo.png',
        '/static/logo.png',
        '/wp-content/uploads/logo.png', // WordPress common
      ];

      // Try HTTPS images first (most reliable for CORS)
      for (const path of commonPaths) {
        const ok = await tryImage(`https://${host}${path}`);
        if (ok) return setAndCache('reachable');
      }

      // Try HTTP images
      for (const path of commonPaths.slice(0, 3)) { // Only try first 3 for HTTP
        const ok = await tryImage(`http://${host}${path}`);
        if (ok) return setAndCache('reachable');
      }

      // Last resort: no-cors fetch (if this succeeds, site exists but blocks images/CORS)
      const fetchOk = await tryFetch(`https://${host}`);
      if (fetchOk) return setAndCache('reachable');

      const fetchOk2 = await tryFetch(`http://${host}`);
      if (fetchOk2) return setAndCache('reachable');

      return setAndCache('unreachable');
    } catch {
      return setAndCache('unreachable');
    }
  };

  // Kick off probe in an effect to avoid setState during render
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        const host = new URL(violation.url).hostname.replace(/^www\./,'');
        const status = await probeReachability(host);
        if (!mounted) return;
        if (status === 'reachable' || status === 'unreachable') setReachability(status);
      } catch {
        if (mounted) setReachability('unreachable');
      }
    };
    if (reachability === 'unknown') {
      run();
    }
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [violation.url]);

  return (
    <div className={`violation-card ${violation.reviewed ? 'reviewed' : ''}`}>
      <div className="violation-header">
        <div className="violation-domain">
          {onToggleSelect && (
            <input
              type="checkbox"
              checked={!!isSelected}
              onChange={() => onToggleSelect(violation._id)}
            />
          )}
          <a
            href={violation.url}
            target="_blank"
            rel="noopener noreferrer"
            className="domain-link"
          >
            {violation.domain}
          </a>
          <span className="external-icon">↗</span>
          {violation.verified === false && (
            <span className="verification-badge unverified" title="Site was unreachable - detected from search results only">
              ⚠ Unverified
            </span>
          )}
          {violation.verified === true && (
            <span className="verification-badge verified" title="Site was successfully crawled and analyzed">
              ✓ Verified
            </span>
          )}
          {reachability !== 'unknown' && (
            <span className={`reachability-badge ${reachability}`} title={reachability === 'reachable' ? 'Site responds to automated probes from your browser (images, fetch)' : 'Site blocks automated probes (CORS, anti-bot, or slow). May still be accessible manually.'}>
              {reachability === 'reachable' ? 'Client ✓' : 'Client ✗'}
            </span>
          )}
          <RegionalBadges
            statusMap={{
              sa: violation.reachableSAStatus,
              ae: violation.reachableAEStatus,
              eg: violation.reachableEGStatus,
            }}
            size="compact"
            className="region-inline"
          />
        </div>

        <div className="confidence-badge" style={{ backgroundColor: confidenceColor }}>
          {violation.confidenceScore}% - {confidenceLevel}
        </div>
      </div>

      <div className="violation-content">
        <p className="violation-blurb">{violation.blurb}</p>

        {violation.pageTitle && (
          <p className="page-title">
            <strong>Page Title:</strong> {violation.pageTitle}
          </p>
        )}

        <div className="violation-signals">
          <strong>Detected Signals:</strong>
          <div className="signals-list">
            {violation.signals.map((signal: string, idx: number) => {
              const label = signal.replace(/_/g, ' ');
              const help: Record<string, string> = {
                known_piracy_domain: 'Host matches known piracy patterns/domains',
                video_player: 'Streaming player detected on the page',
                video_player_direct: 'Direct media file/stream (mp4/m3u8) detected',
                video_player_ugc: 'UGC platform embed (e.g., YouTube/Dailymotion)',
                download_links: 'Links/buttons indicate downloadable media',
                piracy_keywords: 'Piracy keywords present near the content title',
                suspicious_ads: 'Ad networks typically seen on piracy sites',
                streaming_embeds: 'Third‑party streaming embed services found',
                piracy_structure: 'Page structure typical of episode/movie aggregators',
                episode_list: 'Season/Episode index list detected',
                trailer_or_promo: 'Trailer/promo cues present (reduces confidence)',
                search_piracy_keywords: 'Search snippet contained piracy indicators',
                unreachable_site: 'Site unreachable; flagged from search context only',
                fallback_classification: 'Classified from search only (unverified)'
              };
              return (
                <span key={idx} className="signal-tag" title={help[signal] || 'Detection signal'}>
                  {label}
                </span>
              );
            })}
          </div>
        </div>

        {/* LLM Reasoning */}
        {violation.llmReasoning && (
          <details className="llm-reasoning">
            <summary>🤖 Why was this flagged?</summary>
            <div className="reasoning-content">
              <p>{violation.llmReasoning}</p>
              {violation.classificationMethod && (
                <span className="classification-badge">{violation.classificationMethod}</span>
              )}
            </div>
          </details>
        )}

        {/* Hosting & Registrar Information */}
        {violation.hostingLookupSuccess && (
          <div className="hosting-registrar-container">
            {/* Hosting Information - Left Half */}
            <div className="hosting-info">
              <strong>🌐 Hosting Information:</strong>
              {violation.hostingCached && (
                <span className="cached-badge" title={`Cached hosting info${violation.hostingLookupDate ? ' · last checked '+ new Date(violation.hostingLookupDate).toLocaleString() : ''}`}>cached</span>
              )}
              <div className="hosting-details">
                {violation.hostingProvider && (
                  <div className="hosting-row">
                    <span className="hosting-label">Provider:</span>
                    <span className="hosting-value">{violation.hostingProvider}</span>
                  </div>
                )}

                {violation.ipAddress && (
                  <div className="hosting-row">
                    <span className="hosting-label">IP Address:</span>
                    <span className="hosting-value">{violation.ipAddress}</span>
                  </div>
                )}

                {violation.serverLocation && (
                  <div className="hosting-row">
                    <span className="hosting-label">Location:</span>
                    <span className="hosting-value">{violation.serverLocation}</span>
                  </div>
                )}

                {violation.asn && (
                  <div className="hosting-row">
                    <span className="hosting-label">ASN:</span>
                    <span className="hosting-value">{violation.asn} ({violation.asnOrg || 'Unknown'})</span>
                  </div>
                )}

                {violation.hostingAbuseEmail && (
                  <div className="hosting-row">
                    <span className="hosting-label">Abuse Contacts:</span>
                    <div className="abuse-emails">
                      <a href={`mailto:${violation.hostingAbuseEmail}`} className="abuse-email">
                        📧 {violation.hostingAbuseEmail} <span className="email-type">(HOSTING)</span>
                      </a>
                      {violation.abuseEmails && violation.abuseEmails.length > 0 && (
                        <details className="additional-emails">
                          <summary>View {violation.abuseEmails.length} additional contact(s)</summary>
                          <div className="additional-emails-list">
                            {violation.abuseEmails.map((email: string, idx: number) => (
                              <a key={idx} href={`mailto:${email}`} className="abuse-email">
                                📧 {email}
                              </a>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Registrar Information - Right Half */}
            {(violation.registrar || violation.registrationDate || violation.whoisPrivacy !== undefined || violation.registrarAbuseEmail) && (
              <div className="registrar-info">
                <strong>📋 Registrar Information:</strong>
                <div className="hosting-details">
                  {violation.registrar && (
                    <div className="hosting-row">
                      <span className="hosting-label">Registrar:</span>
                      <span className="hosting-value">{violation.registrar}</span>
                    </div>
                  )}

                  {violation.registrationDate && (
                    <div className="hosting-row">
                      <span className="hosting-label">Registered:</span>
                      <span className="hosting-value">{new Date(violation.registrationDate).toLocaleDateString()}</span>
                    </div>
                  )}

                  {violation.whoisPrivacy !== undefined && (
                    <div className="hosting-row">
                      <span className="hosting-label">WHOIS Privacy:</span>
                      <span className={`hosting-value ${violation.whoisPrivacy ? 'whois-privacy' : ''}`}>
                        {violation.whoisPrivacy ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  )}

                  {violation.registrarAbuseEmail && (
                    <div className="hosting-row">
                      <span className="hosting-label">Abuse Contact:</span>
                      <div className="abuse-emails">
                        <a href={`mailto:${violation.registrarAbuseEmail}`} className="abuse-email">
                          📧 {violation.registrarAbuseEmail} <span className="email-type">(REGISTRAR)</span>
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {violation.hostingLookupSuccess === false && (
          <div className="hosting-info-error">
            <strong>⚠️ Hosting Lookup Failed:</strong> {violation.hostingLookupError || 'Unable to retrieve hosting information'}
          </div>
        )}

        <div className="screenshot-section">
          {screenshotUrl ? (
            <>
              <button
                onClick={() => setShowScreenshot(!showScreenshot)}
                className="btn-screenshot"
              >
                {showScreenshot ? 'Hide' : 'Show'} Screenshot
              </button>

              {showScreenshot && (
                <div className="screenshot-container">
                  <img
                    src={screenshotUrl}
                    alt={`Screenshot of ${violation.domain}`}
                    className="screenshot-image"
                  />
                  <a
                    href={screenshotUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="screenshot-link"
                  >
                    Open in new tab
                  </a>
                </div>
              )}
            </>
          ) : (
            <div className="screenshot-placeholder">
              <div>No screenshot available</div>
              <button
                className="btn-secondary"
                onClick={handleCaptureScreenshot}
                disabled={captureState === 'loading'}
              >
                {captureState === 'loading' ? 'Capturing…' : 'Capture Screenshot'}
              </button>
            </div>
          )}
          {captureMessage && (
            <div className={`screenshot-status ${captureState}`}>
              {captureMessage}
            </div>
          )}
        </div>
      </div>

      <div className="violation-footer">
        <div className="violation-meta">
          <span className="detected-time">
            Detected: {new Date(violation.detectedAt).toLocaleString()}
          </span>
        </div>
        <div className="violation-actions">
          <button className="btn-plain" onClick={(e) => { e.stopPropagation(); setShowEvidence(true); }} title="Open evidence (screenshot, links)">🧾 Open Evidence</button>
          <button onClick={() => setShowDmcaModal(true)} className="btn-plain" title="Send DMCA takedown email">⚖️ Send Takedown</button>
          {violation.falsePositive ? (
            <button onClick={() => restoreViolation({ violationId: violation._id })} className="btn-plain">Restore</button>
          ) : (
            <button
              onClick={handleMarkNotPirate}
              className="btn-plain"
              title="Dismiss and reduce future scores for this domain/pattern (feeds the classifier’s negative priors)"
            >
              ✖ Mark as Not Pirate
            </button>
          )}
          <button onClick={handleMarkReviewed} className="btn-plain">{violation.reviewed ? '✓ Reviewed' : 'Mark as Reviewed'}</button>
        </div>
      </div>

      {showDmcaModal && (
        <DMCAEmailModal
          domain={violation.domain}
          onClose={() => setShowDmcaModal(false)}
        />
      )}

      {showEvidence && createPortal(
        (
          <div className="evidence-overlay" onClick={() => setShowEvidence(false)}>
            <div className="evidence-drawer" onClick={(e) => e.stopPropagation()}>
              <div className="evidence-header">
                <div>
                  <div className="evidence-title">Evidence: {violation.domain}</div>
                  <a href={violation.url} target="_blank" rel="noopener noreferrer" className="evidence-link">{decodeUrl(violation.url)}</a>
                </div>
                <button className="evidence-close" onClick={() => setShowEvidence(false)}>✕</button>
              </div>
              <div className="evidence-body" ref={evidenceBodyRef}>
                {screenshotUrl ? (
                  <div className="evidence-screenshot"><img src={screenshotUrl} alt="Screenshot" /></div>
                ) : (
                  <div className="evidence-noscreenshot">
                    <p>No screenshot available</p>
                    <button
                      className="btn-secondary"
                      onClick={handleCaptureScreenshot}
                      disabled={captureState === 'loading'}
                    >
                      {captureState === 'loading' ? 'Capturing…' : 'Capture Screenshot'}
                    </button>
                    {captureMessage && (
                      <div className={`screenshot-status ${captureState}`}>{captureMessage}</div>
                    )}
                  </div>
                )}
                <div className="evidence-info">
                  {violation.pageTitle && (<div><strong>Title:</strong> {violation.pageTitle}</div>)}
                  {violation.blurb && (
                    <div style={{ marginTop: '0.75rem', marginBottom: '0.75rem' }}>
                      <strong>Page Snippet:</strong>
                      <div style={{ marginTop: '0.25rem', fontSize: '0.9rem', lineHeight: '1.5', color: '#4b5563' }}>
                        {violation.blurb}
                      </div>
                    </div>
                  )}
                  <div><strong>Confidence:</strong> {violation.confidenceScore}% ({confidenceLevel})</div>
                  <div><strong>Signals:</strong> {violation.signals.map((s: string) => s.replace(/_/g,' ')).join(', ')}</div>
                </div>
              </div>
            </div>
          </div>
        ),
        document.body
      )}
    </div>
  );
}
